import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { LOGO } from "../data/site";
import { Lock, RefreshCw, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem("srw_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Admin | Sterling Roofworks"; }, []);

  const load = useCallback(async (k) => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Key": k };
      const [l, s] = await Promise.all([
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/leads/stats`, { headers }),
      ]);
      setLeads(l.data); setStats(s.data); setAuthed(true);
      sessionStorage.setItem("srw_admin", k);
    } catch (e) {
      toast.error("Invalid admin key");
      setAuthed(false);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (key) load(key); }, [key, load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}?status=${status}`, {}, { headers: { "X-Admin-Key": key } });
      load(key);
    } catch { toast.error("Update failed"); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D] p-4">
        <Toaster theme="dark" position="top-center" />
        <div className="glass rounded-md p-8 w-full max-w-sm text-center">
          <img src={LOGO} alt="Sterling" className="w-40 mx-auto mb-6" />
          <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
            <Lock size={20} className="text-[#D4AF37]" />
          </div>
          <h1 className="font-head text-2xl uppercase tracking-tight mb-5">Admin Access</h1>
          <input data-testid="admin-key-input" type="password" value={key} onChange={(e) => setKey(e.target.value)}
            placeholder="Enter admin key" onKeyDown={(e) => e.key === "Enter" && load(key)}
            className="w-full bg-[#0D0D0D] border border-white/15 rounded-sm px-4 py-3 mb-4 focus:outline-none focus:border-[#D4AF37]" />
          <button data-testid="admin-login" onClick={() => load(key)} className="btn-gold rounded-sm w-full py-3 text-sm">Unlock Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] py-10">
      <Toaster theme="dark" position="top-center" />
      <div className="container-lux">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="Sterling" className="h-12" />
            <h1 className="font-head text-2xl uppercase tracking-tight">Leads Dashboard</h1>
          </div>
          <button data-testid="admin-refresh" onClick={() => load(key)} className="btn-ghost rounded-sm px-4 py-2 text-sm inline-flex items-center gap-2">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          <div className="glass rounded-md p-6"><p className="font-head text-4xl text-gold-metallic">{stats.total}</p><p className="text-sm text-[#C8C8C8] uppercase tracking-wider mt-1">Total Leads</p></div>
          <div className="glass rounded-md p-6"><p className="font-head text-4xl text-gold-metallic">{stats.new}</p><p className="text-sm text-[#C8C8C8] uppercase tracking-wider mt-1">New</p></div>
        </div>

        <div className="space-y-4" data-testid="admin-leads-list">
          {leads.length === 0 && <p className="text-[#C8C8C8]">No leads yet.</p>}
          {leads.map((l) => (
            <div key={l.id} data-testid="admin-lead-row" className="glass rounded-md p-5">
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <p className="font-head text-xl uppercase tracking-tight">{l.name}
                    <span className={`ml-3 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${l.status === "new" ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-white/10 text-[#C8C8C8]"}`}>{l.status}</span>
                  </p>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#C8C8C8]">
                    <a href={`tel:${l.telephone}`} className="inline-flex items-center gap-1 hover:text-[#D4AF37]"><Phone size={13} /> {l.telephone}</a>
                    {l.email && <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-[#D4AF37]"><Mail size={13} /> {l.email}</a>}
                    {l.service && <span>· {l.service}</span>}
                  </div>
                  {l.address && <p className="text-sm text-[#C8C8C8] mt-1">{l.address}</p>}
                  {l.message && <p className="text-sm text-[#F8F8F8]/80 mt-2">{l.message}</p>}
                  <p className="text-xs text-white/30 mt-2">{new Date(l.created_at).toLocaleString()} · {l.source}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => setStatus(l.id, "contacted")} className="btn-ghost rounded-sm px-3 py-1.5 text-xs">Mark Contacted</button>
                  <button onClick={() => setStatus(l.id, "new")} className="btn-ghost rounded-sm px-3 py-1.5 text-xs">Mark New</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
